'use client';

import React from 'react';
import {Mobile} from '@components/Mobile';
import {MediaQuery} from '@constants/theme';
import {Slide} from '@mui/material';
import {TransitionProps} from '@mui/material/transitions';
import MuiButton from '@new-components/atoms/MuiButton';
import AreaSelector from '@new-components/molecules/AreaSelector';
import PriceFilter from '@new-components/molecules/PriceFilter';
import PropertyTypeSelector from '@new-components/molecules/PropertyTypeSelector';
import AvailabilitySelector from '@new-components/organisms/AvailabilitySelector';
import BedroomAdvanceFilter from '@new-components/organisms/BedroomAdvanceFilter';
import PropertyViewSelector from '@new-components/organisms/PropertyViewSelector';
import SaleTypeSelector from '@new-components/organisms/SaleTypeSelector';
import {useIsDesktop} from '@utils/useisdesktop';
import {MultiSelectSearchBox} from '@new-components/molecules/MultiSelectSearchBox';
import MuiBox from '@new-components/atoms/MuiBox';
import BathroomAdvanceFilter from '@new-components/organisms/BathroomAdvanceFilter';
import {
  useSearchState,
  SearchState,
  IconMap,
} from '../SearchBox/useSearchState';
import {useSearchOptions} from '../SearchBox/useSearchOptions';
import MuiDialog, {
  MuiDialogActions,
  MuiDialogContent,
  MuiDialogTitle,
} from '@new-components/atoms/MuiDialog';
import MuiSvgIcon from '@new-components/atoms/MuiSvgIcon';
import {useRouter} from 'next/navigation';
import {getQueryStr} from '../SearchBox';
import {getAppRoute} from '@utils/getAppRoute';
import {navigate} from '@utils/navigate';

export type AdvancedFilterData = SearchState;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='left' ref={ref} {...props} />;
});

type Props = {
  open: boolean;
  onClose: (value?: AdvancedFilterData) => void;
  data?: AdvancedFilterData;
};
export function AdvancedFilters(props: Props) {
  const [state, dispatch] = useSearchState(props.data);
  const {suggestions, filters} = useSearchOptions();
  const isDesktop = useIsDesktop();
  // const router = useRouter();
  // const handleSearch = () => {
  //   const queryStr = getQueryStr(state);
  //   navigate(router, getAppRoute('UNITS', `?${queryStr}`));
  // };

  const handleApplySearch = () => {
    // handleSearch();
    props.onClose(state);
  };

  return (
    <MuiDialog
      open={props.open}
      onClose={() => props.onClose()}
      maxWidth={'sm'}
      fullWidth
      TransitionComponent={Transition}
      fullScreen={isDesktop ? false : true}
      scroll='paper'
      sx={{
        [MediaQuery.desktop]: {
          '& .MuiDialog-container': {
            justifyContent: 'flex-end',
          },
          '& .MuiPaper-root': {
            height: '100%',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridTemplateRows: 'min-content',
            borderRadius: '24px',
          },
        },
      }}>
      <Mobile>
        <MuiButton
          sx={{
            backgroundColor: 'rgba(95, 89, 89, 0.8)',
            backgroundBlendMode: 'overlay',
            backdropFilter: 'blur(80px)',
            width: '32px',
            height: '32px',
            borderRadius: '100px',
            minWidth: '32px',
            margin: '24px 24px 0px auto',
          }}
          onClick={() => props.onClose()}>
          <MuiSvgIcon>
            <svg
              width='18'
              height='18'
              viewBox='0 0 18 18'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M14.2504 3.75L3.75037 14.25M3.75037 3.75L14.2504 14.25'
                stroke='white'
                strokeWidth='1.25'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </MuiSvgIcon>
        </MuiButton>
      </Mobile>
      <MuiDialogTitle
        sx={{
          fontSize: '24px',
          fontWeight: 500,
          lineHeight: '30px',
          letterSpacing: -0.4000000059604645,
          textAlign: 'left',
          textUnderlinePosition: 'from-font',
          textDecorationSkipInk: 'none',
          color: '#2D2D2D',
          borderRadius: '0px',
          paddingInline: '24px',
        }}>
        All Filters
      </MuiDialogTitle>
      <MuiDialogActions
        sx={{
          paddingInline: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          [MediaQuery.mobile]: {
            position: 'fixed',
            bottom: '0px',
            width: '100%',
            paddingBlock: '24px',
            justifyContent: 'space-between',
            boxShadow: '0px 8px 32px 0px #00000033',
          },
        }}>
        <MuiButton
          sx={{
            fontSize: '17px',
            fontWeight: 400,
            lineHeight: '23.8px',
            letterSpacing: -0.4000000059604645,
            textAlign: 'center',
            textDecorationLine: 'underline',
            textDecorationStyle: 'solid',
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            color: '#2D2D2D',
            background: 'transparent',
            textDecoration: 'underline',
            ['&:hover']: {
              background: 'transparent',
            },
          }}
          onClick={() => dispatch({type: 'RESET_STATE', payload: undefined})}>
          Reset
        </MuiButton>
        <MuiButton
          sx={{
            boxShadow: '0px 8px 32px 0px #00000033',
            color: '#FFFFFF',
            background: '#2D2D2D',
            ['&:hover']: {
              background: '#2D2D2D',
            },
          }}
          onClick={handleApplySearch}>
          Apply
        </MuiButton>
      </MuiDialogActions>
      <MuiDialogContent
        sx={{
          gridColumn: 'span 2',
          paddingInline: '24px',
          marginBottom: '84px',
          paddingBlock: '0px',
          ['& > *']: {
            paddingBlock: '24px',
            borderBottom: '1px solid #82807E1A',
          },
          ['&:last-child']: {
            borderBottom: 'none',
          },
          ['& > :first-child']: {
            paddingBlock: '0',
            paddingBottom: '24px',
          },
          [MediaQuery.desktop]: {
            marginBottom: 0,
          },
        }}>
        <Mobile>
          <MuiBox
            sx={{
              position: 'relative',
              ['& > * > :first-child']: {
                paddingBlock: '16px',
                background: '#F5F4F2',
                ['input']: {
                  background: '#F5F4F2',
                },
              },
            }}>
            <MultiSelectSearchBox
              placeholder='Destination or Community'
              options={suggestions}
              value={state.searchText}
              onChange={(value) => {
                dispatch({type: 'SET_SEARCH_TEXT', payload: value});
              }}
              selectionLimit={5}
              iconMap={IconMap}
              showSearchIcon={true}
            />
          </MuiBox>
        </Mobile>
        <PropertyTypeSelector
          options={filters.types.map(({iconType, ...type}) => ({
            ...type,
            icon: IconMap[iconType] ?? IconMap.default,
          }))}
          title='Property Type'
          subTitle='Can choose multiple'
          selectedTypes={state.propertyType}
          setSelectedTypes={(value) =>
            dispatch({type: 'SET_PROPERTY_TYPE', payload: value})
          }
        />
        <AvailabilitySelector
          label='Availability'
          options={filters.completionStatus}
          value={state.availability}
          handleChange={(value) =>
            dispatch({type: 'SET_AVAILABILITY', payload: value})
          }
        />
        <BedroomAdvanceFilter
          title='Beds'
          subTitle='Can choose multiple'
          options={filters.bedrooms}
          value={state.bedrooms}
          handleChange={(value) =>
            dispatch({type: 'SET_BEDROOMS', payload: value})
          }
        />
        <BathroomAdvanceFilter
          title='Baths'
          subTitle='Can choose multiple'
          options={filters.bathrooms}
          value={state.bathrooms}
          handleChange={(value) =>
            dispatch({type: 'SET_BATHROOMS', payload: value})
          }
        />
        <PriceFilter
          hasReset={false}
          label='Price Range'
          value={state.price}
          handleChange={(value) =>
            dispatch({type: 'SET_PRICE', payload: value})
          }
          min={filters.price.min}
          max={filters.price.max}
          step={1}
        />
        <AreaSelector
          label='Size (sqft)'
          value={state.area}
          handleChange={(value) => dispatch({type: 'SET_AREA', payload: value})}
          min={filters.area.min}
          max={filters.area.max}
          step={1}
        />
        <PropertyViewSelector
          label='Property View'
          options={filters.unitView}
          value={state.propertyView}
          handleChange={(value) =>
            dispatch({type: 'SET_PROPERTY_VIEW', payload: value})
          }
        />
        <SaleTypeSelector
          label='Sale Type'
          options={filters.resaleTypes}
          value={state.saleType}
          handleChange={(value) =>
            dispatch({type: 'SET_SALE_TYPE', payload: value})
          }
        />
      </MuiDialogContent>
    </MuiDialog>
  );
}
